// The online help: the builder's complete user manual, inside the app
// (task 6, Massimo 2026-10-10). Chapters are Markdown files in ./pages,
// bundled with the UI; the vocabulary table is read live from the API.
// Public: no sign-in needed.
import { useEffect, useMemo, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { marked } from "marked";
import { Icon, Logo } from "../workbench/icons";
import { setTheme, theme } from "../lib/theme";
import { lang, t } from "../i18n";

const files = import.meta.glob("./pages/*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

export interface Chapter {
  slug: string;
  title: string;
  md: string;
  headings: { id: string; text: string }[];
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export const chapters: Chapter[] = Object.keys(files)
  .sort()
  .map((path) => {
    const md = files[path];
    const slug = path.replace(/^.*\/\d+-/, "").replace(/\.md$/, "");
    const title = md.match(/^# (.+)$/m)?.[1] ?? slug;
    const headings = [...md.matchAll(/^## (.+)$/gm)].map((m) => ({ id: slugify(m[1]), text: m[1] }));
    return { slug, title, md, headings };
  });

// headings get ids (for the "On this page" links)
marked.use({
  renderer: {
    heading({ tokens, depth }) {
      const text = this.parser.parseInline(tokens);
      const plain = text.replace(/<[^>]+>/g, "");
      return `<h${depth} id="${slugify(plain)}">${text}</h${depth}>\n`;
    },
  },
});

interface Vocab {
  version: string;
  roles: { name: string; label: string; kind?: string; value_types: string[]; description?: string; deprecated?: boolean }[];
  kinds: { name: string; label: string; default: string; examples: string[] }[];
  qualifiers: { name: string; description: string; values?: string[] }[];
}

function VocabularyTables() {
  const [v, setV] = useState<Vocab | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch("/api/v1/vocabulary").then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))).then(setV).catch((e) => setErr(String(e)));
  }, []);
  if (err) return <p class="text-muted">{t("proj.help.vocab.error", { error: err })}</p>;
  if (!v) return <p class="text-muted">{t("proj.help.vocab.loading")}</p>;
  return (
    <div class="help-prose">
      <p>Vocabulary <strong>{v.version}</strong>.</p>
      <h3 id="vocabulary-roles">Roles</h3>
      <table>
        <thead><tr><th>Role</th><th>Label</th><th>Kind</th><th>Value types</th></tr></thead>
        <tbody>
          {v.roles.filter((r) => !r.deprecated).map((r) => (
            <tr><td><code>{r.name}</code></td><td>{r.label}{r.description ? ` - ${r.description}` : ""}</td><td>{r.kind ?? "-"}</td><td>{r.value_types.join(", ")}</td></tr>
          ))}
        </tbody>
      </table>
      <h3 id="vocabulary-kinds">Kinds of quantity</h3>
      <table>
        <thead><tr><th>Kind</th><th>Default unit</th><th>Other units</th></tr></thead>
        <tbody>
          {v.kinds.map((k) => (
            <tr><td>{k.label || k.name} <code>{k.name}</code></td><td><code>{k.default}</code></td><td>{k.examples.filter((e) => e !== k.default).map((e) => <code class="mr-1.5">{e}</code>)}</td></tr>
          ))}
        </tbody>
      </table>
      <h3 id="vocabulary-qualifiers">Qualifiers</h3>
      <table>
        <thead><tr><th>Qualifier</th><th>Says</th><th>Recommended values</th></tr></thead>
        <tbody>
          {v.qualifiers.map((q) => (
            <tr><td><code>{q.name}</code></td><td>{q.description}</td><td>{(q.values ?? []).join(", ")}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Article({ chapter, onLink }: { chapter: Chapter; onLink: (e: MouseEvent) => void }) {
  const parts = chapter.md.split("<!-- vocabulary -->");
  const html = useMemo(() => parts.map((p) => marked.parse(p, { async: false }) as string), [chapter.slug]);
  return (
    <article onClick={onLink}>
      <div class="help-prose" dangerouslySetInnerHTML={{ __html: html[0] }} />
      {parts.length > 1 && <VocabularyTables />}
      {html.slice(1).map((h) => <div class="help-prose" dangerouslySetInnerHTML={{ __html: h }} />)}
    </article>
  );
}

export function Help({ slug }: { slug?: string }) {
  const [, nav] = useLocation();
  const [q, setQ] = useState("");
  const [, setDark] = useState(theme());
  const chapter = chapters.find((c) => c.slug === slug) ?? chapters[0];
  const idx = chapters.indexOf(chapter);
  const hits = q.trim()
    ? chapters
        .map((c) => {
          const i = c.md.toLowerCase().indexOf(q.trim().toLowerCase());
          return i < 0 ? null : { c, snippet: c.md.slice(Math.max(0, i - 50), i + 90).replace(/[#*`|]/g, "").replace(/\s+/g, " ") };
        })
        .filter((x): x is { c: Chapter; snippet: string } => !!x)
    : [];

  useEffect(() => {
    document.title = t("proj.help.doctitle", { title: chapter.title });
    const h = location.hash.slice(1);
    if (h) document.getElementById(h)?.scrollIntoView();
    else document.getElementById("help-main")?.scrollTo(0, 0);
  }, [chapter.slug, lang()]);

  // links inside the manual stay in the app
  const onLink = (e: MouseEvent) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href") ?? "";
    if (href.startsWith("/help")) {
      e.preventDefault();
      nav(href);
    } else if (href.startsWith("http")) {
      a?.setAttribute("target", "_blank");
      a?.setAttribute("rel", "noopener");
    }
  };

  return (
    <div class="flex h-full flex-col bg-bar text-ink">
      <header class="flex h-12 flex-none items-center gap-3 border-b border-line px-3">
        <a href="/" class="flex items-center gap-2.5 text-ink no-underline">
          <Logo />
          <span class="leading-tight"><span class="block text-[14px] font-semibold">gIoTHub</span><span class="block text-[11px] text-dim">Type Builder</span></span>
        </a>
        <span class="ml-3 text-[13px] text-muted">{t("proj.help.title")}</span>
        <div class="flex flex-1 justify-center">
          <label class="flex h-8 w-full max-w-[460px] items-center gap-2.5 rounded-lg border border-line2 bg-side px-3 text-dim">
            <Icon name="search" size={15} stroke={2} />
            <input aria-label={t("proj.help.search")} placeholder={t("proj.help.search")} value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)} class="flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-dim" />
          </label>
        </div>
        <button type="button" class="btn btn-ghost w-[30px] justify-center px-0" aria-label={t("proj.help.theme")}
          onClick={() => { const next = theme() === "dark" ? "light" : "dark"; setTheme(next); setDark(next); }}>
          <Icon name={theme() === "dark" ? "sun" : "moon"} size={15} />
        </button>
        <a href="/" class="btn">{t("proj.help.open")}</a>
      </header>
      <div class="flex min-h-0 flex-1">
        <nav aria-label={t("proj.help.chapters")} class="hidden w-[272px] flex-none overflow-y-auto border-r border-line bg-side px-2 py-4 md:block">
          <div class="sec px-3 pb-2">{t("proj.help.manual")}</div>
          {chapters.map((c, i) => (
            <a href={`/help/${c.slug}`} onClick={(e) => { e.preventDefault(); setQ(""); nav(`/help/${c.slug}`); }}
              aria-current={c === chapter ? "page" : undefined}
              class={`tree h-8 no-underline ${c === chapter ? "tree-sel" : ""}`}>
              <span class="w-5 text-right font-mono text-[11px] text-dim">{i + 1}</span>
              <span class="truncate">{c.title}</span>
            </a>
          ))}
          <div class="sec px-3 pb-2 pt-5">{t("proj.help.try")}</div>
          <a href="/demo" class="tree h-8 no-underline"><span class="w-5" /><span>{t("proj.help.demo")}</span></a>
        </nav>
        <main id="help-main" class="min-w-0 flex-1 overflow-y-auto bg-main">
          <div class="mx-auto flex max-w-[1180px] gap-10 px-5 py-8 sm:px-10">
            <div class="min-w-0 max-w-[820px] flex-1">
              {q.trim() ? (
                <section>
                  <h1 class="mb-4 mt-0 text-[24px] font-semibold">{t("proj.help.searchfor", { q })}</h1>
                  {hits.length === 0 && <p class="text-muted">{t("proj.help.nothing")}</p>}
                  {hits.map(({ c, snippet }) => (
                    <a href={`/help/${c.slug}`} onClick={(e) => { e.preventDefault(); setQ(""); nav(`/help/${c.slug}`); }}
                      class="mb-2 block rounded-lg border border-line bg-side px-4 py-3 text-ink no-underline hover:border-line2">
                      <div class="font-semibold">{c.title}</div>
                      <div class="mt-1 text-[13px] text-muted">…{snippet}…</div>
                    </a>
                  ))}
                </section>
              ) : (
                <>
                  {lang() !== "en" && <p class="mb-4 mt-0 rounded-lg border border-line bg-side px-4 py-2 text-[13px] text-muted">{t("common.manual.english")}</p>}
                  <Article chapter={chapter} onLink={onLink} />
                  <div class="mt-12 flex justify-between gap-4 border-t border-line pt-5 text-[13.5px]">
                    {idx > 0 ? <a href={`/help/${chapters[idx - 1].slug}`} onClick={(e) => { e.preventDefault(); nav(`/help/${chapters[idx - 1].slug}`); }}>← {chapters[idx - 1].title}</a> : <span />}
                    {idx < chapters.length - 1 && <a href={`/help/${chapters[idx + 1].slug}`} onClick={(e) => { e.preventDefault(); nav(`/help/${chapters[idx + 1].slug}`); }}>{chapters[idx + 1].title} →</a>}
                  </div>
                </>
              )}
            </div>
            {!q.trim() && chapter.headings.length > 1 && (
              <aside aria-label={t("proj.help.onpage")} class="sticky top-0 hidden w-[220px] flex-none self-start xl:block">
                <div class="sec pb-2">{t("proj.help.onpage")}</div>
                {chapter.headings.map((h) => (
                  <a href={`#${h.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth" }); }}
                    class="block py-1 text-[13px] text-muted no-underline hover:text-ink">{h.text}</a>
                ))}
              </aside>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
